/**
 * BuildVersionCheck — auto cache invalidation bij nieuwe deploy (#767).
 *
 * Probleem dat dit oplost:
 *  - Browser HTTP-cache kan een oude index.html serveren ondanks no-cache
 *  - Cloudflare CDN of aggressive browsers houden oude bundles vast
 *  - Resultaat: gebruikers zien stale UI met bekende bugs (zoals #731)
 *
 * Werking:
 *  1. Bij build bakt Vite VITE_BUILD_SHA in als constante in de bundle
 *  2. Bij app-start fetchen we /api/config voor de actuele server-buildSha
 *  3. Bij mismatch: cleanup stale localStorage caches + force reload zonder banner
 *
 * Loop-protectie: sessionStorage-flag voorkomt dat een persistente mismatch
 * (bv. server in onbekende staat) de pagina blijft herladen.
 */
import { fetchWithTimeout } from './api/base.ts';

// VITE_BUILD_SHA wordt door Vite bij build ingezet (zie Dockerfile stage 1).
// In dev / lokale builds zonder env-var is dit 'dev'.
const CLIENT_BUILD_SHA = (import.meta.env.VITE_BUILD_SHA as string | undefined) || 'dev';

const STALE_LOCALSTORAGE_PREFIXES = ['af_offline_', 'af_queue_operations'] as const;

// Voorkom reload-loop wanneer mismatch persistent is. Reset bij echte tab-close
// (sessionStorage clears) of bij succesvolle match (uitgevoerd in deze service).
const RELOAD_FLAG = 'pt_build_version_reloaded';

export type BuildCheckResult =
  | 'match'
  | 'mismatch-reload'
  | 'mismatch-loop-skip'
  | 'skipped-dev'
  | 'skipped-server-dev'
  | 'fetch-failed'
  | 'error';

export interface BuildVersionCheckOptions {
  /** Override de geladen client BUILD_SHA — alleen voor tests. */
  clientSha?: string;
  /** Override de fetch-functie — alleen voor tests. */
  fetchConfig?: () => Promise<{ ok: boolean; json: () => Promise<{ buildSha?: string }> }>;
  /** Override location.reload — alleen voor tests. */
  onReload?: () => void;
}

/**
 * Vergelijk geladen client BUILD_SHA met server-side BUILD_SHA.
 * Bij mismatch: ruim stale caches op en triggert reload.
 *
 * Side-effects (localStorage cleanup, sessionStorage flag, reload) zijn via
 * options injecteerbaar zodat tests ze kunnen mocken.
 *
 * Idempotent — meerdere calls binnen dezelfde sessie zijn veilig dankzij de
 * sessionStorage RELOAD_FLAG.
 */
export const runBuildVersionCheck = async (
  opts: BuildVersionCheckOptions = {}
): Promise<BuildCheckResult> => {
  const clientSha = opts.clientSha ?? CLIENT_BUILD_SHA;
  const reloadFn = opts.onReload ?? (() => window.location.reload());
  const fetchFn = opts.fetchConfig ?? (() => fetchWithTimeout('/api/config'));

  // In dev/lokale builds slaan we de check over — VITE_BUILD_SHA is dan 'dev'
  // en server returneert ook 'dev'. Geen mismatch, geen reload gewenst.
  if (clientSha === 'dev') return 'skipped-dev';

  let serverSha: string;
  try {
    const res = await fetchFn();
    if (!res.ok) return 'fetch-failed';
    const cfg = await res.json();
    serverSha = cfg.buildSha || 'dev';
  } catch (err) {
    console.warn('[BuildVersion] check failed:', err);
    return 'error';
  }

  if (serverSha === 'dev') return 'skipped-server-dev';

  if (serverSha === clientSha) {
    // Match — reset reload-flag zodat een eventuele volgende mismatch wel weer
    // tot een reload kan leiden.
    sessionStorage.removeItem(RELOAD_FLAG);
    return 'match';
  }

  // Mismatch gedetecteerd. Voorkom reload-loop.
  if (sessionStorage.getItem(RELOAD_FLAG) === '1') {
    console.warn(
      `[BuildVersion] Mismatch persists na reload (client=${clientSha.slice(0, 7)}, server=${serverSha.slice(0, 7)}). Stop loop.`
    );
    return 'mismatch-loop-skip';
  }

  // Cleanup stale localStorage entries
  const removed: string[] = [];
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key) continue;
    const isStale = STALE_LOCALSTORAGE_PREFIXES.some(
      (prefix) => key === prefix || key.startsWith(prefix)
    );
    if (isStale) {
      localStorage.removeItem(key);
      removed.push(key);
    }
  }

  console.info(
    `[BuildVersion] Mismatch detected (client=${clientSha.slice(0, 7)}, server=${serverSha.slice(0, 7)}). ` +
      `Cleaned ${removed.length} stale entries, reloading…`
  );

  sessionStorage.setItem(RELOAD_FLAG, '1');
  // Stille reload — geen banner, geen prompt.
  reloadFn();
  return 'mismatch-reload';
};

/**
 * Backward-compatible wrapper voor index.tsx. Zonder opties gebruikt het
 * de echte env-var en window.location.reload().
 */
export const checkBuildVersion = (): Promise<void> => runBuildVersionCheck().then(() => undefined);

export interface PeriodicBuildCheckOptions {
  /** Override de check-functie — alleen voor tests. */
  check?: () => Promise<BuildCheckResult>;
}

/**
 * Her-check de build-versie wanneer de tab terug in focus komt (#1065).
 *
 * De start-check (#767) draait maar één keer; een uren-open tab mist daardoor
 * een deploy en crasht bij navigatie op een verdwenen chunk. `visibilitychange`
 * is het veiligste her-check-moment: de gebruiker keert terug naar de tab en is
 * op dat moment niet midden in een actie. Bij een mismatch ververst
 * runBuildVersionCheck stil (met eigen loop-guard) vóór de volgende navigatie.
 *
 * Geeft een cleanup-functie terug.
 */
export const installPeriodicBuildVersionCheck = (
  opts: PeriodicBuildCheckOptions = {}
): (() => void) => {
  const check = opts.check ?? runBuildVersionCheck;

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      void check();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  return () => document.removeEventListener('visibilitychange', onVisibility);
};
